import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Check, CheckCheck, ExternalLink, AlertTriangle, Shield, Clock, FileText, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config/api';

const API = API_BASE;

// WebSocket connection manager
class NotificationWebSocket {
  constructor(token, onMessage, onStatusChange) {
    this.token = token;
    this.onMessage = onMessage;
    this.onStatusChange = onStatusChange;
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.pingInterval = null;
  }

  connect() {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const url = `${wsUrl}/api/regulatory/ws${this.token ? `?token=${this.token}` : ''}`;

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.onStatusChange?.('connected');
        
        // Start ping interval
        this.pingInterval = setInterval(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.onMessage?.(data);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.onStatusChange?.('disconnected');
        clearInterval(this.pingInterval);
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.onStatusChange?.('error');
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.attemptReconnect();
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Reconnecting... attempt ${this.reconnectAttempts}`);
      setTimeout(() => this.connect(), this.reconnectDelay * this.reconnectAttempts);
    }
  }

  disconnect() {
    clearInterval(this.pingInterval);
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  send(message) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }
}

// Severity badge colors
const getSeverityColor = (severity) => {
  switch (severity) {
    case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'medium': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  }
};

// Time ago formatter
const timeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

export default function NotificationCenter() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [loading, setLoading] = useState(false);
  const wsRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/regulatory/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  // Handle WebSocket messages
  const handleWsMessage = useCallback((data) => {
    if (data.type === 'new_alert') {
      // Add to notifications list
      const newNotification = {
        notification_id: `temp_${Date.now()}`,
        type: 'new_alert',
        title: data.notification?.title || 'New Alert',
        message: data.alert?.title || '',
        data: data.alert,
        read: false,
        created_at: data.timestamp || new Date().toISOString(),
        link: `/regulatory-alerts?alert=${data.alert?.alert_id}`
      };
      
      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + 1);

      // Show browser notification if permitted
      if (Notification.permission === 'granted') {
        new Notification(data.notification?.title || 'New Regulatory Alert', {
          body: data.alert?.title,
          icon: '/favicon.ico',
          tag: data.alert?.alert_id
        });
      }
    } else if (data.type === 'unread_count') {
      setUnreadCount(data.count);
    }
  }, []);

  // Initialize WebSocket connection
  useEffect(() => {
    if (user && token) {
      wsRef.current = new NotificationWebSocket(token, handleWsMessage, setWsStatus);
      wsRef.current.connect();

      // Request notification permission
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }

      // Fetch initial notifications
      fetchNotifications();

      return () => {
        wsRef.current?.disconnect();
      };
    }
  }, [user, token, handleWsMessage, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(
        `${API}/api/regulatory/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev =>
        prev.map(n =>
          n.notification_id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.post(
        `${API}/api/regulatory/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Navigate to notification link
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.notification_id);
    }
    
    // Handle different notification types
    if (notification.type === 'new_study_material' && notification.course_id) {
      navigate(`/courses/${notification.course_id}/ai-learn`);
    } else if (notification.type === 'course_generation_complete' && notification.course_id) {
      navigate(`/courses/${notification.course_id}/ai-learn?mode=edit`);
    } else if (notification.link) {
      window.location.href = notification.link;
    }
    setIsOpen(false);
  };
  
  // Get icon for notification type
  const getNotificationIcon = (notification) => {
    switch (notification.type) {
      case 'new_study_material':
        return <FileText className="h-4 w-4 text-blue-400" />;
      case 'course_generation_complete':
        return <BookOpen className="h-4 w-4 text-green-400" />;
      case 'course_generation_failed':
        return <AlertTriangle className="h-4 w-4 text-red-400" />;
      default:
        return notification.data?.severity === 'critical' 
          ? <AlertTriangle className="h-4 w-4 text-red-400" />
          : <Shield className="h-4 w-4 text-amber-400" />;
    }
  };
  
  // Get background color for notification type
  const getNotificationBgColor = (notification) => {
    switch (notification.type) {
      case 'new_study_material':
        return 'bg-blue-500/20';
      case 'course_generation_complete':
        return 'bg-green-500/20';
      case 'course_generation_failed':
        return 'bg-red-500/20';
      default:
        return notification.data?.severity === 'critical' ? 'bg-red-500/20' :
               notification.data?.severity === 'high' ? 'bg-orange-500/20' :
               'bg-amber-500/20';
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-slate-800 transition-colors"
        data-testid="notification-bell"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <Bell className={`h-5 w-5 ${wsStatus === 'connected' ? 'text-slate-300' : 'text-slate-500'}`} />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-xs font-bold bg-red-500 text-white rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        
        {/* Connection Status Indicator */}
        <span className={`absolute bottom-1 right-1 w-2 h-2 rounded-full ${
          wsStatus === 'connected' ? 'bg-emerald-500' : 
          wsStatus === 'error' ? 'bg-red-500' : 'bg-slate-500'
        }`} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 max-h-[500px] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800/50">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-amber-400" />
              <h3 className="font-semibold text-white">Notifications</h3>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-slate-400 hover:text-white transition-colors flex items-center gap-1"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="h-3 w-3" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-slate-700 rounded transition-colors"
              >
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-amber-500 border-t-transparent rounded-full" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                <Bell className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs mt-1">New alerts will appear here</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.notification_id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-4 py-3 border-b border-slate-800 cursor-pointer transition-colors ${
                    notification.read 
                      ? 'bg-slate-900/50 hover:bg-slate-800/50' 
                      : 'bg-slate-800/30 hover:bg-slate-800/50'
                  }`}
                  data-testid={`notification-item-${notification.notification_id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`p-2 rounded-lg ${getNotificationBgColor(notification)}`}>
                      {getNotificationIcon(notification)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className={`text-sm font-medium truncate ${
                          notification.read ? 'text-slate-400' : 'text-white'
                        }`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            notification.type === 'new_study_material' ? 'bg-blue-500' :
                            notification.type === 'course_generation_complete' ? 'bg-green-500' :
                            'bg-amber-500'
                          }`} />
                        )}
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-slate-600 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {timeAgo(notification.created_at)}
                        </span>
                        {notification.type === 'new_study_material' && (
                          <span className="text-xs px-1.5 py-0.5 rounded border border-blue-500/30 text-blue-400 bg-blue-500/10">
                            Study Material
                          </span>
                        )}
                        {notification.data?.severity && (
                          <span className={`text-xs px-1.5 py-0.5 rounded border ${getSeverityColor(notification.data.severity)}`}>
                            {notification.data.severity}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Link indicator for actionable notifications */}
                    {(notification.link || notification.course_id) && (
                      <ExternalLink className="h-4 w-4 text-slate-600 flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-700 bg-slate-800/50">
              <a
                href="/regulatory-alerts"
                className="text-xs text-amber-400 hover:text-amber-300 transition-colors"
              >
                View all alerts →
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
